import { createApi } from "@reduxjs/toolkit/query/react";

import { ITechOperationAssetTypeDto } from "../../../pages/tech-operations/shared/dtos/tech-operation-asset-type.dto";
import { ITechOperationAssetDto } from "../../../pages/tech-operations/shared/dtos/tech-operation-asset.dto";
import { TechOperationAsset } from "../../../pages/tech-operations/shared/models/tech-operation-asset";
import { TechOperationAssetType } from "../../../pages/tech-operations/shared/models/tech-operation-asset-type";
import { baseQuery } from "../../../shared/api/base-query";
import { pagedQueryFn } from "../../../shared/api/paged-query-fn";
import type { QuerySubscriptionOptions } from "../../../shared/api/query-subscription-options";
import { rtkqFetchAllPages } from "../../../shared/api/rtkq-fetch-all-pages";
import { buildFilterCondition } from "../../../shared/utils";
import { ITechAssetWithRelationsDto, TechAssetWithRelationsModel } from "../shared";

import { safeSortAssetsByName } from "./techassets.slice";
import { transformApiErrorResponse } from "./techop-assets.rtkq.types";

export const techAssetsApiSlice = createApi({
  reducerPath: "api-tech-assets",
  // https://github.com/reduxjs/redux-toolkit/discussions/2347#discussioncomment-2978038
  refetchOnFocus: false,
  keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
  tagTypes: ["TechAssetsForTechopId"],
  baseQuery,
  endpoints: (builder) => ({
    fetchTechAssetsWithDeleted: builder.query<TechAssetWithRelationsModel[], void>({
      queryFn: pagedQueryFn<ITechAssetWithRelationsDto, TechAssetWithRelationsModel[]>(
        { url: "TechAssetsV2", params: { includeDeleted: true } },
        (dtos) => {
          safeSortAssetsByName(dtos);
          return dtos.map((dto) => {
            const model = new TechAssetWithRelationsModel(dto.id);
            model.updateFromDto(dto);
            return model;
          });
        }
      ),
    }),

    fetchAssetTypes: builder.query<TechOperationAssetType[], void>({
      queryFn: pagedQueryFn<ITechOperationAssetTypeDto, TechOperationAssetType[]>(
        { url: "AssetTypesV2" },
        (dtos) =>
          dtos.map((dto) => {
            const assetType = new TechOperationAssetType(dto.id);
            assetType.updateFromDto(dto);
            return assetType;
          })
      ),
    }),

    fetchAssets: builder.query<TechOperationAsset[], string>({
      queryFn: async (techOperationId, _queryApi, _extraOptions, executeBaseQuery) => {
        const response = await rtkqFetchAllPages<
          ITechOperationAssetDto,
          TechOperationAsset[]
        >(
          executeBaseQuery,
          {
            url: "AssetsV2",
            params: {
              filter: buildFilterCondition("techOperationId", "==", techOperationId),
            },
          },
          (dtos) => dtos.map((dto) => TechOperationAsset.fromAssetsV2Dto(dto))
        );
        if ("error" in response) {
          return { error: transformApiErrorResponse(response.error) };
        }
        return response;
      },
      providesTags: ["TechAssetsForTechopId"],
    }),
  }),
});

const useTechAssetsEndpoint =
  techAssetsApiSlice.endpoints.fetchTechAssetsWithDeleted.useQuery;

export function useFetchTechAssetsWithDeleted(
  options?: QuerySubscriptionOptions<typeof useTechAssetsEndpoint>
) {
  return useTechAssetsEndpoint(undefined, options);
}

export const useLazyFetchTechAssetsWithDeleted =
  techAssetsApiSlice.endpoints.fetchTechAssetsWithDeleted.useLazyQuery;

const useAssetTypesEndpoint = techAssetsApiSlice.endpoints.fetchAssetTypes.useQuery;

export function useFetchAssetTypes(
  options?: QuerySubscriptionOptions<typeof useAssetTypesEndpoint>
) {
  return useAssetTypesEndpoint(undefined, options);
}

export const useLazyFetchAssetTypes =
  techAssetsApiSlice.endpoints.fetchAssetTypes.useLazyQuery;

const useAssetsEndpoint = techAssetsApiSlice.endpoints.fetchAssets.useQuery;

export function useFetchAssets(
  techOperationId: Parameters<typeof useAssetsEndpoint>[0],
  options?: QuerySubscriptionOptions<typeof useAssetsEndpoint>
) {
  return useAssetsEndpoint(techOperationId, options);
}

export const invalidateTechAssetsForTechopId = () => {
  return techAssetsApiSlice.util.invalidateTags(["TechAssetsForTechopId"]);
};
